import { useXactimateMapping } from "@/hooks/useXactimateMapping";
import type { LineItem, Tuple } from "@/interfaces/estimate-detail";
import type { Estimation } from "@/interfaces/estimation.interface";
import { estimationSelector } from "@/redux/selectors/estimation.selector";
import { setEstimationStateAction } from "@/redux/slices/estimation.slice";
import { useDispatch, useSelector } from "@/redux/store";
import { getStorageFileUrl } from "@/util/get-storage-file-url";
import type { JSX } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ImageCanvas } from "../../ImageCrop";
import { CommentSection } from "../CommentSection";
import { LineItemIssues } from "../LineItemIssues";
import type { Category } from "../Rooms/Lines";
import { Wrench, ClipboardList, Sparkle } from "lucide-react";
import { Photos } from "./Photos";
import { Camera } from "lucide-react";
import { ScopeNotes } from "./ScopeNotes";
import { ExcelCellHighlighter } from "../ExcelTableComponent";
import { useEstimateDetail } from "@/hooks/useEstimateDetail";
import { PdfPagesWithHighlights } from "../PdfPagesWithHighlights";
import { MappingDisplayDetails } from "../MappingDisplayDetails";
import { AuditTrail } from "./AuditTrail/AuditTrail";
import { AgentProvenance } from "./AgentProvenance";
import { findAgentCreatedDiff } from "./AuditTrail/AuditTrail.utils";
import { GenerationNotes } from "./AuditTrail/GenerationNotes";
import { useShowInternalDebugInfo } from "@/hooks/useShowInternalDebugInfo";
import { formatRuleCounts, getRuleLabelCountsFromAuditTrail } from "../util/rule-labels";
import { LineItemDiffPath } from "@rebuild/shared";

interface LineSheetsProps extends Pick<Estimation, "files"> {
  label?: string;
  hideHeader?: boolean;
  region: Tuple["tupleRegion"];
  pageImage?: string;
  category?: Category;
  updateLineItemComment: (pageId: string, tupleId: string, lineItemId: string, lineItemUniqueId: string, comment: string) => Promise<void>;
  estimateId: string;
  isTimeAndMaterial?: boolean;
}

interface SheetsProps {
  region: Tuple["tupleRegion"];
  pageImage: string;
  category?: Category;
}

export const LineSheetsHeaderSection = ({ title, children }: { title?: string; children: JSX.Element }) => (
  <h1 className="text-muted-foreground flex items-center px-4">
    <div className="flex h-6 w-6 items-center">{children}</div>
    {title && <div className="text-sm font-medium">{title}</div>}
  </h1>
);

const Sheets = ({ pageImage, region, category }: SheetsProps) => {
  const dispatch = useDispatch();
  const filePath = getStorageFileUrl(pageImage);

  const onClick = () => {
    const orderedFiles = [filePath];

    dispatch(
      setEstimationStateAction({
        region,
        selectedFiles: orderedFiles,
        previewCategory: category,
      }),
    );
  };
  return (
    <div className="flex flex-col gap-2">
      <LineSheetsHeaderSection title="Tic Sheets">
        <ClipboardList className="h-4 w-4" />
      </LineSheetsHeaderSection>
      <div className="grid max-h-full w-full flex-grow gap-4 px-4 py-2">
        <div onClick={onClick} className={`flex cursor-pointer items-center justify-center rounded-sm border border-[#EEEFF1] shadow-md`}>
          <ImageCanvas
            className={`${category === "issues"} rounded-sm`}
            url={filePath}
            rect={region}
            paddingPixels={category === "issues" ? { vertical: 50, horizontal: 5 } : 10}
          />
        </div>
        {/* ))} */}
      </div>
    </div>
  );
};

const RecommendationDetails = ({ lineItem }: { lineItem?: LineItem }) => {
  const explanation = lineItem?.explanation;

  if (!explanation) return <></>;
  return (
    <div className="flex flex-col space-y-4">
      <LineSheetsHeaderSection title="Recommendation explanation">
        <Sparkle className="h-4 w-4 text-blue-600" />
      </LineSheetsHeaderSection>

      <p className="px-4 pb-4 text-sm">{explanation}</p>
    </div>
  );
};

const RemoveRuleReasons = () => {
  const lineItemDetail = useSelector(estimationSelector.lineItemDetail);
  const exclusionReasonText = useMemo(() => {
    if (lineItemDetail?.lineItem.removedByTPARule) {
      const ruleLabelCounts = getRuleLabelCountsFromAuditTrail(lineItemDetail.lineItem.auditTrail ?? [], {
        pathsToLookAt: [LineItemDiffPath.RemovedByTPARule],
      });
      return formatRuleCounts("This line item is excluded by", ruleLabelCounts);
    }
    if (lineItemDetail?.lineItem.isExcludedBy?.length) {
      return `Excluded because line item "${lineItemDetail.lineItem.isExcludedBy[0].lineItem}" is in the same room.`;
    }
    if (lineItemDetail?.lineItem.isMutuallyExcluded) {
      return `Excluded because of a line item that already exists in the estimate.`;
    }
    return null;
  }, [lineItemDetail]);

  return (
    <div className="flex flex-col space-y-4 pt-4">
      <LineSheetsHeaderSection title={"Exclusion explanation"}>
        <Wrench className="h-5 w-5 text-purple-600" />
      </LineSheetsHeaderSection>

      <p className="px-4 text-sm">{exclusionReasonText}</p>
    </div>
  );
};

export const LineSheets = ({ pageImage, region, category, estimateId, updateLineItemComment, isTimeAndMaterial }: LineSheetsProps) => {
  const lineItemDetail = useSelector(estimationSelector.lineItemDetail);
  const { cat, selCode } = useXactimateMapping(lineItemDetail?.lineItem?.lineItemId ?? "", lineItemDetail?.lineItem?.lineItem ?? "");

  const CategorySection = useMemo(() => {
    switch (category) {
      case "issues":
        return <LineItemIssues />;
      case "recommended":
        return <RecommendationDetails lineItem={lineItemDetail?.lineItem} />;
      case "removeRules":
        return <RemoveRuleReasons />;
      default:
        return <RecommendationDetails lineItem={lineItemDetail?.lineItem} />;
    }
  }, [category, lineItemDetail?.lineItem]);

  const updateComment = useCallback(
    async (comment: string) => {
      updateLineItemComment(
        lineItemDetail?.pageId ?? "",
        lineItemDetail?.tuple.tupleId ?? "",
        lineItemDetail?.lineItem.lineItemId ?? "",
        lineItemDetail?.lineItem.uniqueId ?? "",
        comment,
      );
    },
    [
      lineItemDetail?.lineItem.lineItemId,
      lineItemDetail?.pageId,
      lineItemDetail?.tuple.tupleId,
      lineItemDetail?.lineItem.uniqueId,
      updateLineItemComment,
    ],
  );

  const { estimation } = useEstimateDetail(estimateId);
  const showInternalDebugInfo = useShowInternalDebugInfo();
  const agentCreatedDiff = useMemo(() => findAgentCreatedDiff(lineItemDetail?.lineItem.auditTrail), [lineItemDetail]);

  const [photoSelected, setPhotoSelected] = useState(false);
  // we need a way to reset every time the user selects a different line item, this is currently
  // our best of doing this
  useEffect(() => {
    setPhotoSelected(false);
  }, [lineItemDetail]);

  return (
    <div className="flex h-full w-full flex-col py-4">
      {!photoSelected && (
        <div className="shrink-0">
          {CategorySection}
          {!isTimeAndMaterial && <MappingDisplayDetails cat={cat} selCode={selCode} lineItem={lineItemDetail?.lineItem} />}
        </div>
      )}
      {!!lineItemDetail?.lineItem?.photos?.length && (
        <div>
          <LineSheetsHeaderSection title="Photos">
            <Camera className="h-4 w-4" />
          </LineSheetsHeaderSection>

          <Photos
            estimateId={estimateId}
            files={lineItemDetail.lineItem.photos}
            onFileSelect={() => setPhotoSelected(true)}
            onFileClose={() => setPhotoSelected(false)}
            hideRoomNames={true}
          />
        </div>
      )}
      {!photoSelected && (
        <div className="flex flex-col">
          <div className="shrink-0">
            <CommentSection updateComment={updateComment} comment={lineItemDetail?.lineItem?.comment} />
          </div>

          {pageImage && <Sheets pageImage={pageImage} region={region} category={category} />}

          {agentCreatedDiff ? (
            <AgentProvenance diff={agentCreatedDiff} />
          ) : (
            <>
              {!!estimation?.pdfPagesAsImages?.length && (
                <PdfPagesWithHighlights pdfPagesAsImages={estimation.pdfPagesAsImages} showOnlyHighlightedPage />
              )}

              {!pageImage &&
                (estimation?.scopeNoteObjects?.length === 1 ? (
                  <div className="flex h-[50vh] shrink-0 flex-col">
                    <ScopeNotes estimateId={estimateId} showHighlightedWord />
                  </div>
                ) : (
                  <ScopeNotes estimateId={estimateId} showHighlightedWord />
                ))}
              {(lineItemDetail?.lineItem.sheetsFileMappingsList || []).map(({ filePath, cellCoords }) => (
                <ExcelCellHighlighter filePath={filePath} cellsToHighlight={cellCoords} />
              ))}
            </>
          )}

          {showInternalDebugInfo && !!lineItemDetail?.lineItem?.auditTrail?.length && (
            <AuditTrail auditTrail={lineItemDetail.lineItem.auditTrail} />
          )}

          {showInternalDebugInfo && !!lineItemDetail?.lineItem?.generationNotes && (
            <GenerationNotes generationNotes={lineItemDetail.lineItem.generationNotes} />
          )}
        </div>
      )}
    </div>
  );
};
